// ── FrankieOne v2 API Endpoint Catalogue ──────────────────────────────────────

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export type EndpointCategory =
  | 'Utility'
  | 'Individuals'
  | 'Workflow Execution'
  | 'Executions'
  | 'Results'
  | 'Service Profiles'
  | 'Documents'
  | 'IDV'
  | 'Monitoring & Hosted'
  | 'Entity Elements'
  | 'Organizations'
  | 'Platform'

export interface EndpointParam {
  name: string
  description: string
  required: boolean
  defaultValue?: string
}

export interface EndpointDef {
  id: string
  method: HttpMethod
  pathTemplate: string
  summary: string
  description: string
  category: EndpointCategory
  pathParams: EndpointParam[]
  hasRequestBody: boolean
  examplePayload?: unknown
}

export const ENDPOINT_CATEGORIES: EndpointCategory[] = [
  'Utility',
  'Individuals',
  'Workflow Execution',
  'Executions',
  'Results',
  'Service Profiles',
  'Documents',
  'IDV',
  'Monitoring & Hosted',
  'Entity Elements',
  'Organizations',
  'Platform',
]

export const ENDPOINTS: EndpointDef[] = [
  // ── Utility ──────────────────────────────────────────────────────────────────
  {
    id: 'utility-ruok',
    method: 'GET',
    pathTemplate: '/kyc/ruok',
    summary: 'Health check',
    description: 'Verify API credentials and connectivity',
    category: 'Utility',
    pathParams: [],
    hasRequestBody: false,
  },
  {
    id: 'utility-me',
    method: 'GET',
    pathTemplate: '/me',
    summary: 'Validate credentials',
    description: 'Returns current customer account info',
    category: 'Utility',
    pathParams: [],
    hasRequestBody: false,
  },

  // ── Individuals ───────────────────────────────────────────────────────────────
  {
    id: 'individuals-create',
    method: 'POST',
    pathTemplate: '/individuals',
    summary: 'Create individual',
    description: 'Create a new individual entity',
    category: 'Individuals',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      individual: {
        profile: {
          name: [
            {
              displayName: 'TESTONE TEST',
              givenName: 'TESTONE',
              familyName: 'TEST',
              type: 'PRIMARY',
            },
          ],
          dateOfBirth: [
            {
              normalized: '1970-01-01',
              type: 'PRIMARY',
            },
          ],
          addresses: [
            {
              addressType: 'RESIDENTIAL',
              unitNumber: '',
              streetNumber: '1',
              streetName: 'TEST',
              streetType: 'ST',
              suburb: 'SYDNEY',
              state: 'NSW',
              postalCode: '2000',
              country: 'AUS',
            },
          ],
          document: [
            {
              idType: 'PASSPORT',
              country: 'AUS',
              idNumber: 'PA1234567',
              extraData: [
                {
                  kvpKey: 'gender',
                  kvpValue: 'M',
                  kvpType: 'general.string',
                },
              ],
            },
          ],
        },
        consents: [
          {
            consentType: 'GENERAL',
            timestamp: new Date().toISOString(),
          },
          {
            consentType: 'DOCS',
            timestamp: new Date().toISOString(),
          },
        ],
      },
    },
  },
  {
    id: 'individuals-get',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}',
    summary: 'Retrieve individual',
    description: 'Get full individual profile',
    category: 'Individuals',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'individuals-update',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}',
    summary: 'Update individual',
    description: 'Update individual fields',
    category: 'Individuals',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      individual: {
        profile: {
          name: [
            {
              givenName: 'UPDATED',
              familyName: 'TEST',
              type: 'PRIMARY',
            },
          ],
        },
      },
    },
  },
  {
    id: 'individuals-delete',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}',
    summary: 'Delete individual',
    description: 'Permanently remove entity',
    category: 'Individuals',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },

  // ── Workflow Execution ────────────────────────────────────────────────────────
  {
    id: 'workflow-create-execute',
    method: 'POST',
    pathTemplate: '/individuals/new/serviceprofiles/{serviceName}/workflows/{workflowName}/execute',
    summary: 'Create + execute workflow',
    description: 'Create individual and execute KYC in one call',
    category: 'Workflow Execution',
    pathParams: [
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name to execute',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      individual: {
        profile: {
          name: [
            {
              displayName: 'TESTONE TEST',
              givenName: 'TESTONE',
              familyName: 'TEST',
              type: 'PRIMARY',
            },
          ],
          dateOfBirth: [
            {
              normalized: '1970-01-01',
              type: 'PRIMARY',
            },
          ],
          addresses: [
            {
              addressType: 'RESIDENTIAL',
              streetNumber: '1',
              streetName: 'TEST',
              streetType: 'ST',
              suburb: 'SYDNEY',
              state: 'NSW',
              postalCode: '2000',
              country: 'AUS',
            },
          ],
          document: [
            {
              idType: 'PASSPORT',
              country: 'AUS',
              idNumber: 'PA1234567',
            },
          ],
        },
        consents: [
          {
            consentType: 'GENERAL',
            timestamp: new Date().toISOString(),
          },
        ],
      },
      workflowName: 'your_workflow_name',
    },
  },
  {
    id: 'workflow-execute-existing',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute',
    summary: 'Execute workflow on existing',
    description: 'Run workflow on existing entity',
    category: 'Workflow Execution',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name to execute',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      workflowName: 'your_workflow_name',
    },
  },
  {
    id: 'workflow-update-execute',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute',
    summary: 'Update + execute',
    description: 'Update entity and execute in one call',
    category: 'Workflow Execution',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name to execute',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      individual: {
        profile: {
          name: [
            {
              givenName: 'TESTONE',
              familyName: 'TEST',
              type: 'PRIMARY',
            },
          ],
        },
      },
    },
  },
  {
    id: 'workflow-list',
    method: 'GET',
    pathTemplate: '/workflows',
    summary: 'List workflows',
    description: 'List all configured workflows',
    category: 'Workflow Execution',
    pathParams: [],
    hasRequestBody: false,
  },

  // ── Executions ────────────────────────────────────────────────────────────────
  {
    id: 'executions-list',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/executions',
    summary: 'List executions',
    description: 'List all execution history for an entity',
    category: 'Executions',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'executions-get',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/executions/{workflowExecutionId}',
    summary: 'Get execution',
    description: 'Get specific workflow execution result',
    category: 'Executions',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name',
        required: true,
      },
      {
        name: 'workflowExecutionId',
        description: 'The workflow execution identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'executions-override',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/executions/{workflowExecutionId}',
    summary: 'Override execution',
    description: 'Override execution status/verdict',
    category: 'Executions',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name',
        required: true,
      },
      {
        name: 'workflowExecutionId',
        description: 'The workflow execution identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      overallResult: 'PASS',
      comment: 'Manually overridden by analyst',
    },
  },

  // ── Results ───────────────────────────────────────────────────────────────────
  {
    id: 'results-get',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/results',
    summary: 'Get results',
    description: 'Get all check results for entity',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'results-aml',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/results/aml',
    summary: 'Update AML result',
    description: 'Override AML check status',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      status: 'CLEAR',
    },
  },
  {
    id: 'results-idv',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/results/idv',
    summary: 'Update IDV result',
    description: 'Override IDV check status',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      status: 'CLEAR',
    },
  },
  {
    id: 'results-fraud',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/results/fraud',
    summary: 'Update fraud result',
    description: 'Override fraud check status',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      status: 'CLEAR',
    },
  },
  {
    id: 'results-matchlist',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/results/matchlist',
    summary: 'Update matchlist result',
    description: 'Override matchlist check',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      status: 'CLEAR',
    },
  },
  {
    id: 'results-duplicate',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/results/duplicate',
    summary: 'Update duplicate result',
    description: 'Override duplicate check',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      status: 'CLEAR',
    },
  },
  {
    id: 'results-mkyc-create',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/results/mkyc',
    summary: 'Create mKYC result',
    description: 'Submit manual KYC decision',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      result: 'CLEAR',
      comment: 'Manually verified',
    },
  },
  {
    id: 'results-mkyc-invalidate',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/results/mkyc/invalidate',
    summary: 'Invalidate mKYC',
    description: 'Invalidate previous mKYC decision',
    category: 'Results',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },

  // ── Service Profiles ──────────────────────────────────────────────────────────
  {
    id: 'serviceprofiles-get',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}',
    summary: 'Get service profile',
    description: 'Retrieve service profile for entity',
    category: 'Service Profiles',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'serviceprofiles-update',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}',
    summary: 'Update service profile',
    description: 'Modify service profile settings',
    category: 'Service Profiles',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      serviceProfile: {
        tags: ['reviewed'],
      },
    },
  },
  {
    id: 'serviceprofiles-risk',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/serviceprofiles/{serviceName}/risk',
    summary: 'Update risk level',
    description: 'Manually set risk level',
    category: 'Service Profiles',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      riskLevel: 'LOW',
    },
  },
  {
    id: 'serviceprofiles-search',
    method: 'POST',
    pathTemplate: '/search/serviceprofiles',
    summary: 'Search profiles',
    description: 'Search service profiles by criteria',
    category: 'Service Profiles',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      entityId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
  },

  // ── Documents ─────────────────────────────────────────────────────────────────
  {
    id: 'documents-create',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/documents',
    summary: 'Create document',
    description: 'Attach a new identity document',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      document: {
        idType: 'PASSPORT',
        country: 'AUS',
        idNumber: 'PA1234567',
        extraData: [
          {
            kvpKey: 'gender',
            kvpValue: 'M',
            kvpType: 'general.string',
          },
        ],
      },
    },
  },
  {
    id: 'documents-list',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/documents',
    summary: 'List documents',
    description: 'Get all documents for entity',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'documents-get',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/documents/{documentId}',
    summary: 'Get document',
    description: 'Get specific document',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'documentId',
        description: 'The document identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'documents-update',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/documents/{documentId}',
    summary: 'Update document',
    description: 'Modify document fields',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'documentId',
        description: 'The document identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      document: {
        idNumber: 'PA9999999',
      },
    },
  },
  {
    id: 'documents-delete',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/documents/{documentId}',
    summary: 'Delete document',
    description: 'Remove document',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'documentId',
        description: 'The document identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'documents-delete-attachment',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/documents/{documentId}/attachments/{attachmentId}',
    summary: 'Delete attachment',
    description: 'Remove document attachment',
    category: 'Documents',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'documentId',
        description: 'The document identifier',
        required: true,
      },
      {
        name: 'attachmentId',
        description: 'The attachment identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },

  // ── IDV ───────────────────────────────────────────────────────────────────────
  {
    id: 'idv-token',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/actions/idv/token',
    summary: 'Get IDV token',
    description: 'Generate token for IDV SDK',
    category: 'IDV',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {},
  },
  {
    id: 'idv-ocr',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/actions/idv/ocr',
    summary: 'Submit OCR',
    description: 'Submit document for OCR extraction',
    category: 'IDV',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      documentId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
  },
  {
    id: 'idv-process',
    method: 'POST',
    pathTemplate: '/individuals/{entityId}/actions/idv/process',
    summary: 'Process IDV',
    description: 'Trigger IDV processing',
    category: 'IDV',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      documentId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
  },

  // ── Monitoring & Hosted ───────────────────────────────────────────────────────
  {
    id: 'monitor-toggle',
    method: 'PATCH',
    pathTemplate: '/individuals/{entityId}/monitor',
    summary: 'Toggle monitoring',
    description: 'Enable/disable ongoing monitoring',
    category: 'Monitoring & Hosted',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      monitor: true,
    },
  },
  {
    id: 'hosted-generate-url',
    method: 'POST',
    pathTemplate: '/individuals/hostedurl',
    summary: 'Generate hosted URL',
    description: 'Create Hosted OneSDK verification URL',
    category: 'Monitoring & Hosted',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      consentObtained: true,
      oneSDKFlowId: 'your_flow_id',
      redirectUrl: 'https://example.com/complete',
    },
  },

  // ── Entity Elements ───────────────────────────────────────────────────────────
  {
    id: 'elements-delete-name',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/names/{nameId}',
    summary: 'Remove name',
    description: 'Delete name record',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'nameId',
        description: 'The name record identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-dob',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/datesOfBirth/{dateOfBirthId}',
    summary: 'Remove DOB',
    description: 'Delete date of birth',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'dateOfBirthId',
        description: 'The date of birth record identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-address',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/addresses/{addressId}',
    summary: 'Remove address',
    description: 'Delete address record',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'addressId',
        description: 'The address record identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-email',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/emailaddresses/{emailAddressId}',
    summary: 'Remove email',
    description: 'Delete email address',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'emailAddressId',
        description: 'The email address record identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-phone',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/phonenumbers/{phoneNumberId}',
    summary: 'Remove phone',
    description: 'Delete phone number',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'phoneNumberId',
        description: 'The phone number record identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-consent',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/consents/{consentType}',
    summary: 'Remove consent',
    description: 'Delete consent record',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'consentType',
        description: 'The consent type (e.g. GENERAL, DOCS)',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-delete-externalref',
    method: 'DELETE',
    pathTemplate: '/individuals/{entityId}/externalreferences/{referenceId}',
    summary: 'Remove external ref',
    description: 'Delete external reference',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'referenceId',
        description: 'The external reference identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'elements-list-externalrefs',
    method: 'GET',
    pathTemplate: '/individuals/{entityId}/externalreferences',
    summary: 'List external refs',
    description: 'Get all external references',
    category: 'Entity Elements',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },

  // ── Organizations ─────────────────────────────────────────────────────────────
  {
    id: 'organizations-create',
    method: 'POST',
    pathTemplate: '/organizations',
    summary: 'Create organization',
    description: 'Create org entity (usually from lookup token)',
    category: 'Organizations',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      organization: {
        profile: {
          businessName: [
            {
              name: 'Example Pty Ltd',
              type: 'PRIMARY',
            },
          ],
          registration: [
            {
              registrationNumber: '123456789',
              registrationType: 'ABN',
              country: 'AUS',
            },
          ],
        },
      },
    },
  },
  {
    id: 'organizations-lookup',
    method: 'POST',
    pathTemplate: '/organizations/lookup',
    summary: 'Lookup business',
    description: 'Search business registry by name or registration',
    category: 'Organizations',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      name: 'Example Pty Ltd',
      country: 'AUS',
    },
  },
  {
    id: 'organizations-get',
    method: 'GET',
    pathTemplate: '/organizations/{entityId}',
    summary: 'Get organization',
    description: 'Retrieve full org profile with ownership data',
    category: 'Organizations',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'organizations-execute-workflow',
    method: 'POST',
    pathTemplate: '/organizations/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute',
    summary: 'Execute KYB workflow',
    description: 'Run business verification workflow',
    category: 'Organizations',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name to execute',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      workflowName: 'your_kyb_workflow_name',
    },
  },
  {
    id: 'organizations-get-results',
    method: 'GET',
    pathTemplate: '/organizations/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/results',
    summary: 'Get KYB results',
    description: 'Retrieve business verification results',
    category: 'Organizations',
    pathParams: [
      {
        name: 'entityId',
        description: 'The unique entity identifier',
        required: true,
      },
      {
        name: 'serviceName',
        description: 'The service profile name',
        required: true,
        defaultValue: 'DEFAULT',
      },
      {
        name: 'workflowName',
        description: 'The workflow name',
        required: true,
      },
    ],
    hasRequestBody: false,
  },

  // ── Platform ──────────────────────────────────────────────────────────────────
  {
    id: 'platform-audit',
    method: 'GET',
    pathTemplate: '/audit',
    summary: 'List audit events',
    description: 'Get audit trail',
    category: 'Platform',
    pathParams: [],
    hasRequestBody: false,
  },
  {
    id: 'platform-matchlists-list',
    method: 'GET',
    pathTemplate: '/matchlists',
    summary: 'List matchlists',
    description: 'Get all configured matchlists',
    category: 'Platform',
    pathParams: [],
    hasRequestBody: false,
  },
  {
    id: 'platform-matchlists-entries-list',
    method: 'GET',
    pathTemplate: '/matchlists/{matchlistName}/entries',
    summary: 'List matchlist entries',
    description: 'Get entries in a matchlist',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'platform-matchlists-entries-add',
    method: 'POST',
    pathTemplate: '/matchlists/{matchlistName}/entries',
    summary: 'Add matchlist entry',
    description: 'Add new entry to matchlist',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      entry: {
        name: [
          {
            givenName: 'John',
            familyName: 'Doe',
            type: 'PRIMARY',
          },
        ],
        comment: 'Added for fraud review',
      },
    },
  },
  {
    id: 'platform-matchlists-entry-get',
    method: 'GET',
    pathTemplate: '/matchlists/{matchlistName}/entries/{entryId}',
    summary: 'Get matchlist entry',
    description: 'Retrieve specific entry',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
      {
        name: 'entryId',
        description: 'The entry identifier',
        required: true,
      },
    ],
    hasRequestBody: false,
  },
  {
    id: 'platform-matchlists-entry-update',
    method: 'PATCH',
    pathTemplate: '/matchlists/{matchlistName}/entries/{entryId}',
    summary: 'Update matchlist entry',
    description: 'Modify entry',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
      {
        name: 'entryId',
        description: 'The entry identifier',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      entry: {
        comment: 'Updated comment',
      },
    },
  },
  {
    id: 'platform-matchlists-add-entity',
    method: 'POST',
    pathTemplate: '/matchlists/{matchlistName}/entries/addEntity',
    summary: 'Link entity',
    description: 'Link individual to matchlist entry',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      entityId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
  },
  {
    id: 'platform-matchlists-remove-entity',
    method: 'PATCH',
    pathTemplate: '/matchlists/{matchlistName}/entries/removeEntity',
    summary: 'Unlink entity',
    description: 'Remove individual from matchlist entry',
    category: 'Platform',
    pathParams: [
      {
        name: 'matchlistName',
        description: 'The matchlist name',
        required: true,
      },
    ],
    hasRequestBody: true,
    examplePayload: {
      entityId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
  },
  {
    id: 'platform-matchlists-search',
    method: 'POST',
    pathTemplate: '/search/matchlists',
    summary: 'Search matchlists',
    description: 'Search matchlist entries by criteria',
    category: 'Platform',
    pathParams: [],
    hasRequestBody: true,
    examplePayload: {
      name: [
        {
          givenName: 'John',
          familyName: 'Doe',
        },
      ],
    },
  },
]
